import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import type { Account } from '../../types/auth'

export function AccountSelectorPage(): React.ReactElement {
  const { user, accounts, selectAccount, logout } = useAuth()
  const navigate = useNavigate()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Group accounts by parent (groups first, then standalone rooftops)
  const groupAccounts = accounts.filter(a => a.account_type === 'group')
  const rooftopAccounts = accounts.filter(a => a.account_type === 'rooftop')

  // Rooftops under groups
  const rooftopsUnderGroups = rooftopAccounts.filter(a => a.parent_id)
  // Standalone rooftops
  const standaloneRooftops = rooftopAccounts.filter(a => !a.parent_id)

  const handleSelect = async () => {
    if (!selectedId) return

    setError('')
    setIsSubmitting(true)

    const result = await selectAccount(selectedId)

    if (result.success) {
      navigate('/')
    } else {
      setError(result.error || 'Failed to select account')
    }

    setIsSubmitting(false)
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const AccountCard = ({ account, isChild = false }: { account: Account; isChild?: boolean }) => (
    <button
      type="button"
      onClick={() => setSelectedId(account.id)}
      className={`
        w-full p-4 rounded-xl border-2 text-left transition-all duration-200
        ${isChild ? 'ml-8' : ''}
        ${selectedId === account.id
          ? 'border-primary bg-primary/5 shadow-md'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }
      `}
    >
      <div className="flex items-center gap-4">
        {/* Icon */}
        <div className={`
          w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0
          ${account.account_type === 'group'
            ? 'bg-secondary/10 text-secondary'
            : 'bg-primary/10 text-primary'
          }
        `}>
          {account.account_type === 'group' ? (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
            </svg>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-secondary truncate">{account.name}</h3>
          <p className="text-sm text-grey">
            {account.account_type === 'group' ? 'Dealer Group' : 'Rooftop'}
            {account.parent_name && (
              <span className="text-gray-400"> &bull; {account.parent_name}</span>
            )}
          </p>
        </div>

        {/* Selected Indicator */}
        <div className={`
          w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors
          ${selectedId === account.id
            ? 'border-primary bg-primary'
            : 'border-gray-300'
          }
        `}>
          {selectedId === account.id && (
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </div>
    </button>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-secondary to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-secondary to-slate-800 px-8 py-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Select Account</h1>
                <p className="text-white/70 mt-1">
                  Welcome back, {user?.first_name || user?.email}
                </p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="text-white/60 hover:text-white transition-colors text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                </svg>
                Sign out
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              </div>
            )}

            <p className="text-grey mb-6">
              You have access to multiple accounts. Select which one you&apos;d like to work in:
            </p>

            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {/* Groups with their rooftops */}
              {groupAccounts.map(group => (
                <div key={group.id}>
                  <AccountCard account={group} />
                  {/* Rooftops under this group */}
                  {rooftopsUnderGroups
                    .filter(r => r.parent_id === group.id)
                    .map(rooftop => (
                      <div key={rooftop.id} className="mt-2">
                        <AccountCard account={rooftop} isChild />
                      </div>
                    ))
                  }
                </div>
              ))}

              {/* Standalone rooftops */}
              {standaloneRooftops.length > 0 && groupAccounts.length > 0 && (
                <div className="border-t border-gray-200 my-4 pt-4">
                  <p className="text-xs uppercase tracking-wider text-grey mb-3">Independent Rooftops</p>
                </div>
              )}
              {standaloneRooftops.map(rooftop => (
                <AccountCard key={rooftop.id} account={rooftop} />
              ))}
            </div>

            {/* Continue Button */}
            <div className="mt-8">
              <button
                type="button"
                onClick={handleSelect}
                disabled={!selectedId || isSubmitting}
                className="w-full py-3.5 px-4 bg-primary hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:transform-none shadow-lg shadow-primary/30 disabled:shadow-none flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/50 text-sm mt-6">
          You can switch accounts anytime from the dashboard.
        </p>
      </div>
    </div>
  )
}
